// i18n
import i18n from '@/plugins/i18n';

// Utils
import validateCpfOrCnpj from '@/utils/validateCpfOrCnpj';
import { validateCUIT } from '@common/utils/cuitValidator';
import { isValidInternationalPhoneNumber } from '@/utils/phone';
import { POSTAL_CODE_RULES } from '@/constants/customer-international-bank-constants';

const mixinValidationRules = {
  data() {
    return {};
  },
  methods: {
    validationIsRequiredField(value) {
      return !!value || i18n.t('validation.required');
    },
    validationIsRequiredAutocompleteId(value) {
      return !!value?.id || i18n.t('validation.required');
    },
    validationIsRequiredAutocompleteText(value) {
      return !!value || i18n.t('validation.required');
    },
    validationIsRequiredAutocompleteMultiple(value) {
      return !!value?.length || i18n.t('validation.requiredMultiple');
    },
    validationIsRequiredRadioBtn(value) {
      // == de propósito (como antes): o valor do radio pode ser 0/'' além de false
      return !!value || value == false || i18n.t('validation.requiredRadio');
    },
    /**
     * Uma regex mais segura e suficiente para validação de e-mail comum, sem tentar cobrir todos os casos complexos do RFC 5322.
     * documentação https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/email#validation
     * @param {String} value
     * @returns {Boolean|String} Retorna true se o e-mail for válido ou uma mensagem de erro se não for.
     */
    validationIsEmailValid(value) {
      return !value || /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value) || i18n.t('validation.emailInvalid');
    },
    validationIsCpfCnpjValid(value) {
      if (!value || value.length === 0) return true;

      let valid = false;

      if (value.length === 14) valid = validateCpfOrCnpj.validateCPF(value);
      else if (value.length === 18) valid = validateCpfOrCnpj.validateCNPJ(value);

      return valid || i18n.t('validation.cpfCnpjInvalid');
    },
    validationIsCuitValid(value) {
      if (!value) return true;

      return validateCUIT(value) || i18n.t('validation.cuitInvalid');
    },
    validationIsRequiredFieldNumeric(value) {
      const number = parseFloat(
        value
          .toString()
          // removes letters
          .replace(/^\D+/g, '')
          // convert the string to a valid float format
          .replace(/\./g, '')
          .replace(',', '.')
      );

      return !!number || i18n.t('validation.required');
    },
    validationIsRequiredCheckBox(value) {
      return !!value || i18n.t('validation.requiredCheckbox');
    },
    validationIsRequiredFile(value) {
      return !!value?.name || i18n.t('validation.required');
    },
    validationPasswordField(value) {
      return (value && value.trim().length >= 6) || i18n.t('validation.passwordMin6');
    },
    validationIsRequiredFieldNumericAllowZero(value) {
      const number = parseFloat(
        value
          .toString()
          // removes letters
          .replace(/^\D+/g, '')
          // convert the string to a valid float format
          .replace(/\./g, '')
          .replace(',', '.')
      );

      return !!number || number === 0 || i18n.t('validation.required');
    },
    validationIsPhoneValid(value, countryCode) {
      if (!value) return true;

      if (!countryCode) return i18n.t('validation.phoneCountryRequired');

      return (
        isValidInternationalPhoneNumber(countryCode, value) ||
        i18n.t('validation.phoneInvalidDdi')
      );
    },
    /**
     * Retorna uma função de validação de telefone com DDI pré-configurado.
     */
    validationPhoneWithDdi(countryCode) {
      return (value) => this.validationIsPhoneValid(value, countryCode);
    },
    /**
     * Valida código SWIFT/BIC (8 ou 11 caracteres alfanuméricos)
     * Formato: AAAABBCC ou AAAABBCCDDD
     */
    validationIsSwiftCodeValid(value) {
      if (!value) return true;

      const swiftRegex = /^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}([A-Z0-9]{3})?$/i;

      return swiftRegex.test(value) || i18n.t('validation.swiftBicLength');
    },
    /**
     * Valida CLABE mexicana (18 dígitos)
     */
    validationIsClabeValid(value) {
      return this._validateExactDigitLength(value, 18, 'validation.clabeLength');
    },
    /**
     * Valida número de cartão de débito SPEI (16 dígitos)
     */
    validationIsDebitCardNumberValid(value) {
      return this._validateExactDigitLength(value, 16, 'validation.debitCardLength');
    },
    /**
     * Valida CVU argentino (22 dígitos)
     */
    validationIsCvuValid(value) {
      return this._validateExactDigitLength(value, 22, 'validation.cvuLength');
    },
    /**
     * Valida CBU argentino (22 dígitos)
     */
    validationIsCbuValid(value) {
      return this._validateExactDigitLength(value, 22, 'validation.cbuLength');
    },
    /**
     * Valida CVU ou CBU argentino (22 dígitos)
     */
    validationIsCvuCbuValid(value) {
      return this._validateExactDigitLength(value, 22, 'validation.cvuCbuLength');
    },
    _validateExactDigitLength(value, length, errorKey) {
      if (!value) return true;

      const digitsOnly = value.toString().replace(/\D/g, '');

      return digitsOnly.length === length || i18n.t(errorKey);
    },
    /**
     * Valida código postal de acordo com o país selecionado.
     * Se o país tem regra específica, aplica regex do país.
     * Caso contrário, aplica validação genérica (3-10 chars alfanuméricos com pelo menos 1 dígito).
     */
    validationIsPostalCodeValid(value, countryCode) {
      if (!value) return true;

      const cleaned = value.toString().trim();

      if (countryCode) {
        const rule = POSTAL_CODE_RULES[countryCode.toUpperCase()];

        if (rule) return rule.regex.test(cleaned) || i18n.t('validation.postalCodeCountryInvalid', { example: rule.example });
      }

      const genericRegex = /^[A-Za-z0-9][A-Za-z0-9\s-]{1,8}[A-Za-z0-9]$/;

      if (!genericRegex.test(cleaned)) return i18n.t('validation.postalCodeGenericInvalid');

      if (!/\d/.test(cleaned)) return i18n.t('validation.postalCodeNoDigit');

      return true;
    },
    /**
     * Retorna uma função de validação de código postal com país pré-configurado.
     */
    validationPostalCodeWithCountry(countryCode) {
      return (value) => this.validationIsPostalCodeValid(value, countryCode);
    }
  }
};

export default mixinValidationRules;
